'use client';

import { useState, ChangeEvent, MouseEvent, useEffect } from 'react';
import Grid from '@mui/material/Unstable_Grid2';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { isCheckInteger } from '@/lib/BigNumbers';
import { compareObjects } from '@/lib/Compare';
import { readJackpotSettingList } from '@/api/bet/read';
import { ResReadJackpotSettingType } from '@/types/bet/read-type';
import { updateJackpotSetting } from '@/api/bet/update';
import { UpdateJackpotSettingType } from '@/types/bet/update-type';
import { createJackpotSetting, deleteJackpotSetting } from '@/api/bet/create';
import { CreateJackpotSettingType } from '@/types/bet/create-type';
import Header from '../Header';

export default function JackpotSettingForm() {
  // 초기 상태에서도 임시 ID를 생성하여 수정 가능하도록 함
  const getTempId = () => `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const [rows, setRows] = useState<ResReadJackpotSettingType[]>([
    {
      jackpotId: getTempId(),
      rank: '',
      probabilityRatio: '',
      amountRatio: '',
    },
  ]);
  // 서버에서 호출 한 뒤 값을 저장 (rows, oldRows)
  const [oldRows, setOldRows] = useState<ResReadJackpotSettingType[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const re = await readJackpotSettingList();
        // 서버에서 받은 데이터는 그대로 사용 (jackpotId가 이미 있음)
        // 빈 배열이거나 데이터가 없을 때만 초기 행 추가
        if (re.data && re.data.length > 0) {
          // null 값을 빈 문자열로 변환하여 React 경고 방지
          const normalizedData = re.data.map((item: ResReadJackpotSettingType) => ({
            ...item,
            rank: item.rank ?? '',
            probabilityRatio: item.probabilityRatio ?? '',
            amountRatio: item.amountRatio ?? '',
          }));
          setRows(normalizedData);
          setOldRows(normalizedData);
        } else {
          // 데이터가 없을 때만 초기 행 추가
          const initialRow = {
            jackpotId: getTempId(),
            rank: '',
            probabilityRatio: '',
            amountRatio: '',
          };
          setRows([initialRow]);
          setOldRows([initialRow]);
        }
      } catch (error) {
        console.error('잭팟 세팅 목록 조회 실패:', error);
        // 에러 발생 시에도 초기 행은 유지
      }
    })();
  }, []);

  const handleAddInputs = () => {
    // 새 행에는 임시 ID를 생성 (서버에 저장되기 전까지 사용)
    // temp- 접두사로 서버에서 생성된 ID와 구분
    const tempId = getTempId();
    setRows([
      ...rows,
      {
        jackpotId: tempId,
        rank: '',
        probabilityRatio: '',
        amountRatio: '',
      },
    ]);
  };

  const handleDelete = () => {
    if (rows.length > 1) {
      setRows((prevRows) => prevRows.slice(0, -1));
    } else {
      toast.error('최소 1개의 행이 필요합니다.');
    }
  };

  const handleInputChange = (rowId: string, field: keyof ResReadJackpotSettingType, value: string) => {
    if (!rowId || rowId === '') {
      console.error('rowId가 없습니다:', rowId);
      return;
    }

    setRows((prevRows) => {
      // rowId로 특정 행 찾기
      const targetRow = prevRows.find((row) => row.jackpotId === rowId);
      if (!targetRow) {
        console.error('해당 rowId를 가진 행을 찾을 수 없습니다:', rowId, prevRows);
        return prevRows;
      }

      // 숫자 필드 검증 (rank, probabilityRatio, amountRatio 필드)
      if (field === 'rank' || field === 'probabilityRatio' || field === 'amountRatio') {
        if (value !== '' && !isCheckInteger(value)) {
          toast.error('소수점을 사용할 수 없습니다.');
          return prevRows;
        }
        // rank, probabilityRatio, amountRatio는 마이너스 값 방지
        if (value !== '') {
          const numValue = Number(value);
          if (numValue < 0) {
            toast.error('마이너스 값을 입력할 수 없습니다.');
            return prevRows;
          }
        }
      }

      // 특정 행의 특정 필드만 업데이트 (엄격한 비교)
      return prevRows.map((row) => {
        if (row.jackpotId === rowId) {
          return { ...row, [field]: value };
        }
        return row;
      });
    });
  };

  const handleUpdate = async (event: MouseEvent<HTMLButtonElement>) => {
    console.log('잭팟 세팅 업데이트');
    console.log('현재 rows:', rows);
    console.log('oldRows:', oldRows);

    try {
      // 1단계: DELETE - oldRows에는 있지만 rows에는 없는 행 삭제
      const deletedRows = oldRows.filter(
        (oldRow) => !oldRow.jackpotId.startsWith('temp-') && !rows.find((row) => row.jackpotId === oldRow.jackpotId),
      );
      console.log('DELETE 대상 행:', deletedRows);

      const deletePromises = deletedRows.map(async (row) => {
        console.log('행 삭제:', row.jackpotId);
        return await deleteJackpotSetting(row.jackpotId);
      });

      // 2단계: CREATE - temp-로 시작하는 ID는 새로 생성
      const createRows = rows.filter((row) => row.jackpotId.startsWith('temp-'));
      console.log('CREATE 대상 행:', createRows);

      const createPromises = createRows.map(async (row) => {
        const createData: CreateJackpotSettingType = {
          rank: row.rank,
          probabilityRatio: row.probabilityRatio,
          amountRatio: row.amountRatio,
        };
        console.log('새 행 생성:', createData);
        return await createJackpotSetting(createData);
      });

      // 3단계: UPDATE - 기존 행의 변경사항 업데이트
      const updatePromises = rows
        .map((row) => {
          // temp-로 시작하는 행은 제외 (이미 create에서 처리)
          if (row.jackpotId.startsWith('temp-')) {
            return null;
          }

          const oldRow = oldRows.find((old) => old.jackpotId === row.jackpotId);
          if (!oldRow) {
            return null;
          }

          // 변경사항 확인
          const changes = compareObjects(row, oldRow);

          // 변경사항이 있으면 업데이트
          if (Object.keys(changes).length > 0) {
            const updateData: UpdateJackpotSettingType = {
              jackpotId: row.jackpotId,
              ...changes,
            };
            console.log('업데이트 데이터:', updateData);
            return updateJackpotSetting(updateData);
          }

          return null;
        })
        .filter((promise) => promise !== null) as Promise<any>[];

      // DELETE, CREATE, UPDATE를 순차적으로 실행
      const deleteResults = await Promise.all(deletePromises);
      console.log('삭제 결과:', deleteResults);

      const createResults = await Promise.all(createPromises);
      console.log('생성 결과:', createResults);

      const updateResults = await Promise.all(updatePromises);
      console.log('업데이트 결과:', updateResults);

      toast.success('저장이 완료되었습니다.');

      // 저장 후 서버에서 최신 데이터 다시 가져오기
      const re = await readJackpotSettingList();
      if (re.data && re.data.length > 0) {
        // null 값을 빈 문자열로 변환하여 React 경고 방지
        const normalizedData = re.data.map((item: ResReadJackpotSettingType) => ({
          ...item,
          rank: item.rank ?? '',
          probabilityRatio: item.probabilityRatio ?? '',
          amountRatio: item.amountRatio ?? '',
        }));
        setRows(normalizedData);
        setOldRows(normalizedData);
      } else {
        // 데이터가 없을 때만 초기 행 추가
        const initialRow = {
          jackpotId: getTempId(),
          rank: '',
          probabilityRatio: '',
          amountRatio: '',
        };
        setRows([initialRow]);
        setOldRows([initialRow]);
      }
    } catch (error) {
      console.error('저장 실패:', error);
      toast.error('저장에 실패했습니다.');
    }
  };

  return (
    <Card>
      <Header title="잭팟 세팅" />
      <Divider />
      <CardContent
        sx={{
          maxHeight: '70vh',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
            },
          },
        }}
      >
        <Box p={2}>
          <Grid xs={12}>
            <Typography variant="h4">잭팟 세팅</Typography>
          </Grid>
          <Grid container spacing={2} mb={2}>
            <Grid xs={4} sm={4}>
              <Typography variant="h6">랭킹</Typography>
            </Grid>
            <Grid xs={4} sm={4}>
              <Typography variant="h6">당첨비율</Typography>
            </Grid>
            <Grid xs={4} sm={4}>
              <Typography variant="h6">당첨확률</Typography>
            </Grid>
          </Grid>

          {rows.map((row: ResReadJackpotSettingType, index: number) => {
            // 각 행은 이미 jackpotId를 가지고 있어야 함 (useEffect에서 처리)
            const rowId = row.jackpotId;

            return (
              <Grid container spacing={2} key={rowId || `row-${index}`} mb={2}>
                <Grid xs={4} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>랭킹</InputLabel>
                    <OutlinedInput
                      value={row.rank ?? ''}
                      inputProps={{
                        type: 'number',
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        min: 0,
                      }}
                      label="랭킹"
                      name="rank"
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        handleInputChange(row.jackpotId, 'rank', e.target.value);
                      }}
                    />
                  </FormControl>
                  <FormHelperText>※ 숫자만 입력 가능</FormHelperText>
                </Grid>

                <Grid xs={4} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>당첨비율(잭팟 당첨 기준금액 대비 %)</InputLabel>
                    <OutlinedInput
                      value={row.amountRatio ?? ''}
                      inputProps={{
                        type: 'number',
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        min: 0,
                      }}
                      placeholder="당첨비율"
                      label="당첨비율"
                      name="amountRatio"
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        handleInputChange(row.jackpotId, 'amountRatio', e.target.value);
                      }}
                    />
                  </FormControl>
                  <FormHelperText>※ 숫자만 입력 가능</FormHelperText>
                </Grid>

                <Grid xs={4} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>당첨확률(%)</InputLabel>
                    <OutlinedInput
                      value={row.probabilityRatio ?? ''}
                      inputProps={{
                        type: 'number',
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        min: 0,
                      }}
                      label="당첨확률"
                      name="probabilityRatio"
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        handleInputChange(row.jackpotId, 'probabilityRatio', e.target.value);
                      }}
                    />
                  </FormControl>
                  <FormHelperText>※ 숫자만 입력 가능</FormHelperText>
                </Grid>
              </Grid>
            );
          })}

          <Grid container spacing={2} mt={2}>
            <Grid xs={6}>
              <Button fullWidth variant="outlined" color="secondary" onClick={handleAddInputs}>
                추가
              </Button>
            </Grid>
            <Grid xs={6}>
              <Button fullWidth variant="outlined" color="secondary" onClick={handleDelete}>
                삭제
              </Button>
            </Grid>
            <Grid xs={12}>
              <Button fullWidth variant="contained" color="primary" onClick={handleUpdate}>
                잭팟 세팅 업데이트
              </Button>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
}
